import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useSSEAwareQuery } from '../hooks/useSSEAwareQuery';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload,
  Assessment,
  AccountTree,
  Error,
  Schedule,
  Description,
  TableChart,
} from '@mui/icons-material';
import { apiService, Batch, Profile, TransformationTestResponse } from '../services/api';
import { featureFlags } from '../config';
import { hasPreviewRows } from '../utils/preview';
import { getStatusConfig, formatDate } from '../utils/transformationHelpers';
import BatchFlowCard from './workspace/BatchFlowCard';
import StagingOutputCard from './workspace/StagingOutputCard';
import PipelineProgressCard from './workspace/PipelineProgressCard';
import { listBatchPipelineVersions, listProjectPipelines, type PipelineVersion, type ProjectPipeline } from '../services/workspaceApi';
import { useProjectContext } from '../context/ProjectContext';
import PreviewSampleTables from './PreviewSampleTables';
import { useActiveSessionForBatch } from '../hooks/useWorkspaceSession';
import { effectiveOverviewBatchStatus, usesMappingEditorTab } from '../utils/mappingRunStatus';

const BatchDetail: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canvasEnabled = featureFlags.canvasExperiment;
  const previewEnabled = featureFlags.transformationPreview;
  const { targetSchema, selectedProjectId } = useProjectContext();

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewResponse, setPreviewResponse] = useState<TransformationTestResponse | null>(null);
  const [previewNotice, setPreviewNotice] = useState<string | null>(null);

  // Check if we're in iterative mapping mode
  const { data: session } = useActiveSessionForBatch(batchId);

  const [mappingGuideLoading, setMappingGuideLoading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const { data: batch, isLoading, error } = useSSEAwareQuery<Batch>(
    ['batch', batchId],
    () => apiService.getBatch(batchId!),
    {
      enabled: !!batchId,
      pollingInterval: 5_000,
      ssePollingInterval: 30_000,
    }
  );

  const { data: profile } = useQuery<Profile[]>(
    ['profile', batchId],
    () => apiService.getProfile(batchId!),
    { enabled: !!batchId && batch?.status !== 'pending' }
  );

  const { data: status } = useSSEAwareQuery<{ status: string; raw_status?: string; progress?: number; logs?: string[] }>(
    ['status', batchId],
    () => apiService.getTransformationStatus(batchId!),
    {
      enabled: !!batchId && ['transforming', 'loading', 'profiling', 'mapping', 'mapped'].includes(batch?.status || ''),
      pollingInterval: 5_000,
      ssePollingInterval: 5_000,
    }
  );

  const overviewStatus = effectiveOverviewBatchStatus(
    batch?.status === 'mapped' && status?.raw_status === 'mapping'
      ? 'mapping'
      : (batch?.status ?? ''),
    session?.workspace_meta,
  );

  const { data: pipelineVersions = [] } = useQuery<PipelineVersion[]>(
    ['pipeline-versions', batchId],
    () => listBatchPipelineVersions(batchId!),
    { enabled: !!batchId },
  );

  const MAPPED_STATUSES = ['mapped', 'reviewing', 'approved', 'transforming', 'completed'];
  const { data: mappingSummaryResponse } = useQuery(
    ['batch-mappings-summary', batchId],
    () => apiService.getMappings(batchId!),
    {
      enabled: !!batchId && MAPPED_STATUSES.includes(batch?.status ?? ''),
      staleTime: 60_000,
    },
  );
  const targetTables = useMemo(() => {
    const tables = new Set<string>();
    for (const m of mappingSummaryResponse?.mappings ?? []) {
      if (m.target_table) tables.add(m.target_table);
    }
    return Array.from(tables).sort();
  }, [mappingSummaryResponse]);

  const handleViewProfile = () => navigate(`/batches/${batchId}/profile`);
  const showMappingEditor = usesMappingEditorTab(session?.workspace_meta);
  const handleViewMapping = () => {
    navigate(showMappingEditor ? `/batches/${batchId}/mapping` : `/batches/${batchId}/workspace`);
  };

  const handlePreviewClick = async () => {
    if (!batchId || !batch) return;
    setPreviewDialogOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewNotice(null);
    setPreviewResponse(null);
    try {
      const mappingResponse = await apiService.getMappings(batchId);
      const recipeId = mappingResponse?.recipe_id;
      if (!recipeId) {
        setPreviewError('No mapping recipe available for this batch.');
        return;
      }
      let distributedResponse: TransformationTestResponse | null = null;
      try {
        distributedResponse = await apiService.getDistributedPreview(batch.batch_id, recipeId, 50);
      } catch (distributedError) {
        console.warn('Distributed preview failed, attempting local fallback.', distributedError);
      }

      if (distributedResponse && hasPreviewRows(distributedResponse)) {
        setPreviewResponse(distributedResponse);
        return;
      }

      const fallbackResponse = await apiService.getTransformPreview(batch.batch_id, recipeId, 50);
      if (hasPreviewRows(fallbackResponse)) {
        const notice = distributedResponse
          ? 'Distributed preview returned no rows. Showing local preview instead.'
          : 'Distributed preview is unavailable. Showing local preview output.';
        setPreviewResponse(fallbackResponse);
        setPreviewNotice(notice);
        return;
      }

      setPreviewError('Preview completed but no rows were returned. Check mapping expressions or try regenerating.');
    } catch (error) {
      const message = (error as any)?.message || 'Failed to load preview.';
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewResponse(null);
    setPreviewNotice(null);
  };


  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !batch) {
    return (
      <Alert severity="error">
        Failed to load batch details. Please check if the batch ID is correct.
      </Alert>
    );
  }

  const currentStep = getStatusConfig(batch.status).step;
  const canPreview =
    previewEnabled && ['mapped', 'reviewing', 'approved', 'transforming', 'loading', 'completed'].includes(batch.status);

  // Status Banner Helpers
  const getBannerColor = (status: string) => {
    if (status === 'completed') return 'success.light';
    if (status === 'failed') return 'error.light';
    if (['transforming', 'loading', 'profiling', 'mapping'].includes(status)) return 'info.light';
    return 'grey.100';
  };

  const isActiveProcessing = ['transforming', 'loading', 'profiling', 'mapping'].includes(batch.status);
  const isFailed = batch.status === 'failed';
  const isOverviewActiveProcessing = ['transforming', 'loading', 'profiling', 'mapping'].includes(overviewStatus);

  const steps = [
    { label: 'Uploaded', description: 'Data files received' },
    { label: 'Analyzing', description: 'Understanding your data structure' },
    { label: 'Deep Analysis', description: 'Extracting context and patterns' },
    { label: 'Ready to Map', description: 'Review before mapping starts' },
    { label: 'AI Mapping', description: 'AI is generating field mappings' },
    { label: 'Review', description: 'Your review and approval needed' },
    { label: 'Transforming', description: 'Converting your data' },
    { label: 'Complete', description: 'Data is ready to use' },
  ];

  // Map backend status to simplified steps for UI
  const getActiveStepIndex = (status: string) => {
    const map: Record<string, number> = {
      pending: 0,
      profiling: 1,
      profiled: 2,
      summarizing: 2,
      ready_for_mapping: 3,
      mapping: 4,
      mapped: 5,
      reviewing: 5,
      approved: 6, // Approved implies waiting for transformation
      transforming: 6,
      loading: 6,
      completed: 7,
      failed: 7, // Show all steps but fail the last one? or keep at current? 
    };
    return map[status] ?? 0;
  };

  const activeStepUi = getActiveStepIndex(batch.status);

  return (
    <Box sx={{ p: 3 }}>
      {/* 1. Status Banner */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: getBannerColor(overviewStatus),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h5" component="h1" fontWeight="bold">
              {overviewStatus === 'completed' ? 'Transformation Complete' :
                overviewStatus === 'failed' ? 'Transformation Failed' :
                  isOverviewActiveProcessing ? 'Transformation in Progress' : 'Transformation Details'}
            </Typography>
            <Chip
              label={getStatusConfig(overviewStatus).label}
              color={getStatusConfig(overviewStatus).color}
              size="small"
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
            ID: <span style={{ fontFamily: 'monospace' }}>{batch.batch_id}</span>
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          {batch.status === 'failed' && (
            <Button variant="contained" color="error" startIcon={<Error />} onClick={() => setErrorDialogOpen(true)}>
              View Errors
            </Button>
          )}

          {batch.status === 'profiled' && (
            <Button variant="outlined" startIcon={<Assessment />} onClick={handleViewProfile}>
              Profile
            </Button>
          )}

        </Box>
      </Box>

      {/* 2. Active Process Card */}
      {isActiveProcessing && (
        <Card sx={{ mb: 3, borderLeft: '6px solid #1976d2' }} elevation={3}>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <CircularProgress size={40} thickness={4} />
              <Box flexGrow={1}>
                <Typography variant="h6" color="primary">
                  {status?.logs?.length
                    ? status.logs[status.logs.length - 1]
                    : batch.status === 'profiling' ? 'Analyzing your data...'
                      : batch.status === 'mapping' ? 'AI is mapping your fields...'
                        : batch.status === 'transforming' ? 'Transforming your data...'
                          : 'Working on it...'}
                </Typography>
                {status?.progress != null && (
                  <Box display="flex" alignItems="center" mt={1}>
                    <LinearProgress variant="determinate" value={status.progress} sx={{ width: '100%', mr: 2, height: 6, borderRadius: 3 }} />
                    <Typography variant="caption" sx={{ minWidth: 35 }}>{status.progress}%</Typography>
                  </Box>
                )}
                {status?.logs && status.logs.length > 1 && (
                  <Box sx={{ mt: 1, maxHeight: 120, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11 }}>
                    {status.logs.slice(0, -1).map((line: string, i: number) => (
                      <Typography key={i} variant="caption" display="block" color="text.secondary">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 3. Action Required Cards */}
      {batch.status === 'ready_for_mapping' && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<Description fontSize="inherit" />}>
          <Typography variant="subtitle1" gutterBottom>Ready for Mapping</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            Your data has been analyzed. Open the Workspace to explore your data, configure transformations, and start AI-powered mapping.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AccountTree />}
              onClick={() => navigate(`/batches/${batchId}/workspace`)}
            >
              Open Workspace
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/batches/${batchId}/profile`)}
            >
              Review Profile
            </Button>
          </Stack>
        </Alert>
      )}

      {/* 3b. What's Next — Agent CTA cards */}
      {featureFlags.workspaceExperiment && ['profiled', 'summarized', 'ready_for_mapping', 'researching', 'staged'].includes(batch.status) && (
        <BatchFlowCard
          batchId={batch.batch_id}
          batchStatus={batch.status}
          hasTargetSchema={!!batch.target_schema_id || !!targetSchema}
          hasStagingOutput={batch.status === 'staged'}
          onExploreData={() => navigate(`/batches/${batchId}/workspace`)}
          onGenerateMapping={() => navigate(`/batches/${batchId}/workspace`)}
        />
      )}

      {/* 3c. Staging Output — shown when agent pipeline produced staged data */}
      {featureFlags.workspaceExperiment && batch.status === 'staged' && (
        <StagingOutputCard
          outputName={batch.staging_output_name}
          rowCount={batch.staging_row_count}
          colCount={batch.staging_col_count}
          stepCount={batch.staging_step_count}
          changesSummary={batch.staging_changes_summary}
          onViewPipeline={() => navigate(`/batches/${batchId}/workspace`)}
          onPreviewOutput={() => navigate(`/batches/${batchId}/workspace`)}
        />
      )}

      {/* 4. Pipeline Progress */}
      <PipelineProgressCard
        batchStatus={overviewStatus}
        fileCount={batch.total_files || (batch.files?.length ?? 0)}
        mappingCount={targetTables.length || undefined}
        targetTableCount={targetTables.length || undefined}
        targetSchemaName={targetSchema?.schema_name || undefined}
        onClick={(stage) => {
          if (stage === 'mapping' || stage === 'target_tables') {
            handleViewMapping();
          } else if (stage === 'sources' || stage === 'transforms') {
            navigate(`/batches/${batchId}/workspace`);
          }
        }}
      />

      {/* Pipeline Versions */}
      {pipelineVersions.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Pipeline Versions ({pipelineVersions.length})
            </Typography>
            <Stack spacing={0.5}>
              {pipelineVersions.map((pv) => (
                <Box
                  key={pv.pipeline_id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1,
                    borderRadius: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => {
                    if (pv.batch_id) navigate(`/batches/${pv.batch_id}/workspace`);
                  }}
                >
                  <Chip
                    label={`v${pv.version}`}
                    size="small"
                    color={pv.batch_id === batchId ? 'primary' : 'default'}
                    variant={pv.batch_id === batchId ? 'filled' : 'outlined'}
                    sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: 12, flex: 1 }}>
                    {pv.batch_name || `Batch ${(pv.batch_id || '').slice(0, 8)}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
                    {pv.step_count} step{pv.step_count !== 1 ? 's' : ''}
                  </Typography>
                  <Chip
                    label={pv.status}
                    size="small"
                    color={pv.status === 'completed' ? 'success' : pv.status === 'draft' ? 'default' : 'warning'}
                    variant="outlined"
                    sx={{ fontSize: 10, height: 20 }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, minWidth: 80, textAlign: 'right' }}>
                    {formatDate(pv.created_at)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* 5. Main Grid */}
      <Grid container spacing={3}>
        {/* Left: Timeline */}
        <Grid item xs={12} md={8}>
          {/* Mapping Summary (shown when mappings exist) */}
          {targetTables.length > 0 && (
            <Card
              variant="outlined"
              sx={{ mb: 3, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => navigate(`/batches/${batchId}/workspace`)}
            >
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Mapping Summary — {targetTables.length} target table{targetTables.length !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {targetTables.slice(0, 20).map(t => (
                    <Chip key={t} label={t} size="small" sx={{ fontSize: 11 }} />
                  ))}
                  {targetTables.length > 20 && (
                    <Chip label={`+${targetTables.length - 20} more`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right: Stats & Summary */}
        <Grid item xs={12} md={4}>
          {/* Stats Block */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Stats</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Files</Typography>
                  <Typography variant="h6">{batch.total_files || (batch.files?.length ?? 0)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Source Rows</Typography>
                  <Typography variant="h6">{batch.total_rows ? batch.total_rows.toLocaleString() : "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip 
                    title={!batch.processed_rows ? "Rows successfully exported to your target database. Run an export to see this count." : ""} 
                    arrow
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>Output Rows</Typography>
                      <Typography variant="h6">{batch.processed_rows ? batch.processed_rows.toLocaleString() : "—"}</Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{formatDate(batch.created_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Updated</Typography>
                  <Typography variant="body2">{formatDate(batch.updated_at)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Compact Profile Summary */}
          {profile && profile.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Data Profile</Typography>
                <List disablePadding>
                  {profile.map((p, i) => (
                    <ListItem key={i} divider={i < profile.length - 1} sx={{ px: 0 }}>
                      <ListItemText
                        primary={p.file_name}
                        secondary={`${p.columns.length} cols · ${p.row_count.toLocaleString()} rows`}
                        primaryTypographyProps={{ variant: 'subtitle2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Preview Dialog (Unchanged logic, kept clean) */}
      <Dialog open={previewDialogOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>Data Preview</DialogTitle>
        <DialogContent dividers>
          {previewLoading && (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading preview rows...</Typography>
            </Stack>
          )}

          {previewError && !previewLoading && <Alert severity="error">{previewError}</Alert>}

          {!previewLoading && !previewError && previewResponse && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {previewNotice && <Alert severity="info">{previewNotice}</Alert>}
              {!!previewResponse.warnings?.length && (
                <Alert severity="warning">
                  <Box component="ul" sx={{ pl: 3, my: 0 }}>
                    {previewResponse.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </Box>
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                {previewResponse.sample_size} row sample · {previewResponse.dependent_sources.length} sources involved
              </Typography>
              <PreviewSampleTables sampleOutput={previewResponse.sample_output} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Error Details Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Transformation Errors</DialogTitle>
        <DialogContent dividers>
          {batch?.error_message ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {batch.error_message}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The transformation completed but some or all target tables failed to load.
            </Alert>
          )}
          {batch?.processed_rows != null && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Rows processed: <strong>{batch.processed_rows?.toLocaleString()}</strong>
              {batch.failed_rows != null && batch.failed_rows > 0 && (
                <> &middot; Rows failed: <strong style={{ color: 'red' }}>{batch.failed_rows.toLocaleString()}</strong></>
              )}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Check the <strong>Logs</strong> tab for detailed execution history, or retry from the Mapping view.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BatchDetail;
